import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "../../lib/zod";
import { Button, Input, Label } from "@workspace/ui";
import { useAuth } from "../../hooks/use-auth";
import { API_URL } from "../../lib/env";

type DomainStatus = "idle" | "checking" | "valid" | "invalid" | "error";

const normalizeDomain = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "";

  const fromEmail = normalized.includes("@")
    ? normalized.split("@").pop() ?? normalized
    : normalized;

  return fromEmail.replace(/^@+/, "");
};

const getEffectiveDomain = (emailValue?: string, domainValue?: string) => {
  const emailDomain = normalizeDomain(emailValue ?? "");
  if (emailDomain) {
    return emailDomain;
  }
  return normalizeDomain(domainValue ?? "");
};

const createSchema = (t: TFunction<"identity">) => z.object({
  email: z.string().email(t("auth.validation.invalidEmail")),
  password: z.string().min(6, t("auth.validation.passwordMin6")),
  domain: z
    .string()
    .min(1, t("auth.login.domainRequired"))
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, t("auth.login.domainRequired")),
});

type LoginFormData = {
  email: string;
  password: string;
  domain: string;
};

export function LoginPage() {
  const { t } = useTranslation("identity");
  const schema = useMemo(() => createSchema(t), [t]);
  const { login, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<DomainStatus>("idle");
  const [domainCheckMessage, setDomainCheckMessage] = useState<string | null>(
    null,
  );
  const [hasMicrosoftSSO, setHasMicrosoftSSO] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: import.meta.env.PROD ? "" : "admin@favit.es",
      password: import.meta.env.PROD ? "" : "Admin123!",
      domain: "",
    },
  });

  const formDomainValue = watch("domain");
  const formEmailValue = watch("email");
  const normalizedDomain = getEffectiveDomain(formEmailValue, formDomainValue) || null;
  const apiBaseUrl = API_URL.replace(/\/+$/, "");

  const verifyDomain = useCallback(
    async (rawDomain: string, signal?: AbortSignal) => {
      const normalized = normalizeDomain(rawDomain);
      if (!normalized) {
        return {
          status: "invalid" as DomainStatus,
          message: t("auth.login.domainRequired"),
          hasMicrosoftSSO: false,
        };
      }

      const { data } = await axios.get<{ exists: boolean; hasMicrosoftSSO: boolean }>(
        `${apiBaseUrl}/auth/domains/${encodeURIComponent(normalized)}`,
        {
          signal,
        },
      );

      if (data.exists) {
        return {
          status: "valid" as DomainStatus,
          hasMicrosoftSSO: data.hasMicrosoftSSO ?? false,
        };
      }

      return {
        status: "invalid" as DomainStatus,
        message: t("auth.login.domainUnknown"),
        hasMicrosoftSSO: false,
      };
    },
    [apiBaseUrl, t],
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      setError(null);
      const normalizedDomain = getEffectiveDomain(values.email, values.domain);

      if (domainStatus !== "valid") {
        try {
          const result = await verifyDomain(normalizedDomain);
          if (result.status !== "valid") {
            setDomainStatus(result.status);
            setDomainCheckMessage(result.message ?? null);
            setHasMicrosoftSSO(false);
            return;
          }
          setDomainStatus("valid");
          setDomainCheckMessage(null);
          setHasMicrosoftSSO(result.hasMicrosoftSSO ?? false);
        } catch (domainError) {
          if (!axios.isCancel(domainError)) {
            if (axios.isAxiosError(domainError)) {
              if (domainError.code === 'ERR_NETWORK' || domainError.message.includes('Network Error')) {
                setDomainStatus("error");
                setDomainCheckMessage(
                  t("auth.login.networkError"),
                );
              } else {
                setDomainStatus("error");
                setDomainCheckMessage(
                  t("auth.login.domainCheckFailed"),
                );
              }
            } else {
              setDomainStatus("error");
              setDomainCheckMessage(
                t("auth.login.domainCheckFailed"),
              );
            }
            return;
          }
        }
      }

      await login({
        email: values.email,
        password: values.password,
        domain: normalizedDomain,
      });
    } catch (loginError) {
      if (
        axios.isAxiosError(loginError) &&
        loginError.response?.data?.message === "Unknown tenant"
      ) {
        setDomainStatus("invalid");
        setDomainCheckMessage(
          t("auth.login.domainUnknown"),
        );
      }
      setError(t("auth.login.invalidCredentials"));
    }
  });

  useEffect(() => {
    const normalized = normalizedDomain ?? "";

    if (!normalized) {
      setDomainStatus("idle");
      setDomainCheckMessage(null);
      setHasMicrosoftSSO(false);
      return;
    }

    const controller = new AbortController();
    setDomainStatus("checking");
    setDomainCheckMessage(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await verifyDomain(normalized, controller.signal);
        setDomainStatus(result.status);
        setDomainCheckMessage(result.message ?? null);
        setHasMicrosoftSSO(result.hasMicrosoftSSO ?? false);
      } catch (checkError) {
        if (axios.isCancel(checkError)) {
          return;
        }
        if (axios.isAxiosError(checkError)) {
          if (checkError.code === 'ERR_NETWORK' || checkError.message.includes('Network Error')) {
            setDomainStatus("error");
            setDomainCheckMessage(
              t("auth.login.networkError"),
            );
          } else if (checkError.response?.status === 500) {
            setDomainStatus("error");
            setDomainCheckMessage(
              t("auth.login.domainServerError"),
            );
          } else {
            setDomainStatus("error");
            setDomainCheckMessage(
              t("auth.login.domainCheckFailed"),
            );
          }
        } else {
          setDomainStatus("error");
          setDomainCheckMessage(
            t("auth.login.domainCheckFailed"),
          );
        }
      }
    }, 400);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [normalizedDomain, t, verifyDomain]);

  useEffect(() => {
    const extractedDomain = getEffectiveDomain(formEmailValue, formDomainValue);
    if ((formDomainValue ?? "") !== extractedDomain) {
      setValue("domain", extractedDomain, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [formDomainValue, formEmailValue, setValue]);

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
      <div className="mb-4 flex flex-col items-center">
        <img
          src="/assets/Logo_login_light.svg"
          alt="KYVU"
          className="h-[13rem] w-auto max-w-[340px] object-contain dark:hidden"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
        <img
          src="/assets/Logo_login_dark.svg"
          alt="KYVU"
          className="hidden h-[13rem] w-auto max-w-[340px] object-contain dark:block"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      </div>
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 shadow-floating">
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-content-secondary">
              {t("auth.common.email")}
            </Label>
            <Input
              id="email"
              placeholder="email@empresa.com"
              type="email"
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="domain" className="text-content-secondary">
              {t("auth.login.domain")}
            </Label>
            <Input
              id="domain"
              placeholder="empresa.com"
              type="text"
              autoComplete="off"
              readOnly
              className="bg-surface-secondary"
              {...register("domain")}
            />
            {errors.domain && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.domain.message as string}
              </p>
            )}
            {!errors.domain && domainStatus === "checking" && (
              <p className="mt-1 text-xs text-content-muted">
                {t("auth.login.domainChecking")}
              </p>
            )}
            {!errors.domain && domainStatus === "valid" && (
              <p className="mt-1 text-xs text-state-success">
                {t("auth.login.domainFound")}
              </p>
            )}
            {!errors.domain && domainStatus === "invalid" && (
              <p className="mt-1 text-xs text-state-danger">
                {domainCheckMessage ??
                  t("auth.login.domainUnknown")}
              </p>
            )}
            {!errors.domain && domainStatus === "error" && (
              <p className="mt-1 text-xs text-state-warning">
                {domainCheckMessage ??
                  t("auth.login.domainCheckFailed")}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-content-secondary">
              {t("auth.common.password")}
            </Label>
            <Input
              id="password"
              type="password"
              {...register("password")}
            />
            {errors.password && (
              <p className="mt-1 text-xs text-state-danger">
                {errors.password.message}
              </p>
            )}
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-xs text-content-muted transition-colors hover:text-content-primary"
            >
              {t("auth.login.forgotPassword")}
            </button>
          </div>
          {error && <p className="text-sm text-state-danger">{error}</p>}
          <Button
            type="submit"
            className="w-full"
            disabled={
              isLoading ||
              domainStatus === "checking" ||
              domainStatus === "invalid"
            }
          >
            {isLoading ? t("auth.login.signingIn") : t("auth.common.signIn")}
          </Button>
        </form>

        {/* Separador y Botón de Microsoft - Solo si el tenant tiene SSO configurado */}
        {hasMicrosoftSSO && (
          <>
            {/* Separador */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-subtle"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-surface-primary px-2 text-content-muted">
                  {t("auth.login.separator")}
                </span>
              </div>
            </div>

            {/* Botón de Microsoft */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                const normalizedDomain = getEffectiveDomain(
                  formEmailValue,
                  formDomainValue,
                );
                if (!normalizedDomain) {
                  setError(t("auth.login.invalidSsoDomain"));
                  return;
                }
                if (domainStatus !== "valid") {
                  setError(t("auth.login.waitForDomain"));
                  return;
                }
                const callbackUrl = `${window.location.origin}/auth/callback`;
                window.location.href = `${apiBaseUrl}/auth/microsoft?domain=${encodeURIComponent(normalizedDomain)}&redirect_uri=${encodeURIComponent(callbackUrl)}`;
              }}
              disabled={domainStatus === "checking" || domainStatus === "invalid" || domainStatus === "idle"}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="10.5" height="10.5" fill="#F25022" />
                <rect x="12.5" y="0" width="10.5" height="10.5" fill="#7FBA00" />
                <rect x="0" y="12.5" width="10.5" height="10.5" fill="#00A4EF" />
                <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900" />
              </svg>
              {t("auth.login.microsoft")}
            </Button>
          </>
        )}
        {normalizedDomain && (
          <p className="mt-4 text-center text-xs text-content-muted">
            {t("auth.login.activeDomain", { domain: normalizedDomain })}
          </p>
        )}
      </div>
    </div>
  );
}
